import { useCallback, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { debounce, intersection } from 'lodash'

import { useFormValues } from '../../../FormInputComponents/InputField'
import { DEBOUNCE_ONCHANGE_UI_ACTION_TIMEOUT } from '../../constants'

import { MerchantBrandRegexRulesActions, RuleModalActions } from '../../actions'

export default function useFormMerchantBrandRegexFilds(initValues, mode) {
  const dispatch = useDispatch()

  const { values, isFormChanged, handleFieldChange, updateValue, lastChangedFields } = useFormValues(initValues)

  const checkRule = useCallback(
    ({ brandId, merchantId, includeParam, ruleType }) =>
      dispatch(
        MerchantBrandRegexRulesActions.fetchRuleByMainFields({
          merchantId,
          brandId,
          includeParam,
          ruleType
        })
      ),
    [dispatch]
  )

  const checkRuleDebounce = useMemo(
    () =>
      debounce(
        ({ brandId, merchantId, includeParam, ruleType }) => checkRule({ merchantId, brandId, includeParam, ruleType }),
        DEBOUNCE_ONCHANGE_UI_ACTION_TIMEOUT
      ),
    [checkRule]
  )

  // Check rule after first time modal opening
  useEffect(() => {
    const { brandId, merchantId, includeParam, ruleType } = values
    const isAllFieldsFilled = merchantId && brandId && includeParam && ruleType
    if (isAllFieldsFilled && !mode.isRequestView) {
      checkRule({ merchantId, brandId, includeParam, ruleType })
    }
    // Not necessary to check dependencies
    // We run it only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check rule after fields changes
  const { brandId, merchantId, includeParam, ruleType } = values
  useEffect(() => {
    const isAllFieldsFilled = brandId && merchantId && includeParam && ruleType
    const isChangeFilesForCheckRule = intersection(lastChangedFields, ['merchantId', 'brandId']).length > 0
    const isChangeFilesForDebounceCheckRule = intersection(lastChangedFields, ['includeParam', 'ruleType']).length > 0

    // TODO refactor this
    if (isAllFieldsFilled && isChangeFilesForCheckRule) {
      checkRule({ merchantId, brandId, includeParam, ruleType })
    } else if (isAllFieldsFilled && isChangeFilesForDebounceCheckRule) {
      dispatch(RuleModalActions.startRuleLoading()) // immediately show Rule loading
      checkRuleDebounce({ merchantId, brandId, includeParam, ruleType })
    }
  }, [brandId, merchantId, includeParam, ruleType, checkRule, checkRuleDebounce, lastChangedFields, dispatch])

  const handleMerchantChange = useCallback(
    merchant => {
      const merchantId = merchant?.id
      const merchantName = merchant?.merchantName

      updateValue({ merchantId, merchantName })
    },
    [updateValue]
  )

  const handleBrandChange = useCallback(
    brand => {
      // TODO what about some general function for this?
      const brandId = brand?.id
      const brandFullPath = brand?.fullPath
      updateValue({ brandId, brandFullPath })
    },
    [updateValue]
  )

  return { values, isFormChanged, handleFieldChange, handleMerchantChange, handleBrandChange }
}
